// App.tsx — routes and portal entry.
//
// Portal entry is gated twice, deliberately (portal-role-permissions.md §1): the chooser lists only
// what you may enter, AND RequirePortal guards each shell — so a direct URL is closed too, not just
// the menu.
import { Navigate, Route, Routes } from 'react-router-dom'
import { useAuth } from './auth/AuthContext'
import { portalsFor, type PortalKey } from './lib/capabilities'
import PortalChooser from './components/PortalChooser'
import SignIn from './components/SignIn'
import Shell from './components/Shell'
import { Placeholder } from './components/Placeholder'
import TenderRegister from './components/TenderRegister'
import TenderOverviewScreen from './components/TenderOverview'
import CreateTender from './components/CreateTender'
import AmsOverview from './components/ams/AmsOverview'
import AmsQueue from './components/ams/AmsQueue'
import AmsCase from './components/ams/AmsCase'
import AmsApprovals from './components/ams/AmsApprovals'
import AmsRules from './components/ams/AmsRules'
import FieldApp from './components/ams/FieldApp'
import AmsOutreach from './components/ams/AmsOutreach'
import AmsLegal from './components/ams/AmsLegal'
import AmsReports from './components/ams/AmsReports'
import AmsCallCentre from './components/ams/AmsCallCentre'
import AmsInquiry from './components/ams/AmsInquiry'
import AmsGlossary from './components/ams/AmsGlossary'
import AmsDemo from './components/ams/AmsDemo'

function RequirePortal({ portal, children }: { portal: PortalKey; children: JSX.Element }) {
  const { identity, loading, error } = useAuth()
  if (loading) return <div className="page-state">Loading…</div>
  // A failed identity load must not silently degrade into a default view (TD-034).
  if (error) return <div className="page-state page-state-error">{error}</div>
  return portalsFor(identity).includes(portal) ? children : <Navigate to="/" replace />
}

export default function App() {
  return (
    <Routes>
      <Route path="/" element={<PortalChooser />} />

      {/* Town Council */}
      <Route path="/tc/*" element={
        <RequirePortal portal="tc"><Shell portal="tc" /></RequirePortal>
      }>
        <Route index element={<Navigate to="dashboard" replace />} />
        <Route path="dashboard" element={<Placeholder screen="Dashboard" wave="W1" />} />
        <Route path="tenders" element={<TenderRegister />} />
        <Route path="tenders/new" element={<CreateTender />} />
        <Route path="tenders/:id" element={<TenderOverviewScreen />} />
        <Route path="tenders/:id/requirements" element={<Placeholder screen="Requirement set editor" wave="W1" />} />
        <Route path="tenders/:id/matrix" element={<Placeholder screen="Evaluation matrix editor" wave="W1" />} />
        <Route path="tenders/:id/publish" element={<Placeholder screen="Publish" wave="W2" />} />
        <Route path="tenders/:id/box" element={<Placeholder screen="Tender box (sealed)" wave="W2" />} />
        <Route path="tenders/:id/opening" element={<Placeholder screen="Opening ceremony" wave="W4" />} />
        <Route path="tenders/:id/score" element={<Placeholder screen="Scoring workbench" wave="W5" />} />
        <Route path="tenders/:id/consolidate" element={<Placeholder screen="Consolidation" wave="W5" />} />
        <Route path="tenders/:id/recommendation" element={<Placeholder screen="Recommendation paper" wave="W7" />} />
        <Route path="tenders/:id/approval" element={<Placeholder screen="Approval" wave="W7" />} />
        <Route path="suppliers" element={<Placeholder screen="Supplier register" wave="W9" />} />
        <Route path="audit" element={<Placeholder screen="Audit trail" wave="W9" />} />
      </Route>

      {/* Arrears Management.
          A separate portal, not a section of the Town Council one, because most of the people who
          work in it never touch a tender — and an Arrears Manager must never be shown the tender
          system (capabilities.ts::portalsFor). */}
      <Route path="/ams/*" element={
        <RequirePortal portal="ams"><Shell portal="ams" /></RequirePortal>
      }>
        <Route index element={<Navigate to="overview" replace />} />
        <Route path="overview" element={<AmsOverview />} />
        <Route path="queue" element={<AmsQueue />} />
        <Route path="cases/:id" element={<AmsCase />} />
        <Route path="approvals" element={<AmsApprovals />} />
        <Route path="rules" element={<AmsRules />} />
        <Route path="outreach" element={<AmsOutreach />} />
        <Route path="legal" element={<AmsLegal />} />
        <Route path="reports" element={<AmsReports />} />
        {/* House visits is a PAGE OF THIS PORTAL, not a separate application. It was a top-level
            route with its own chrome, which meant it was the one screen with no rail on a desktop
            and no menu button on a phone — the navigation changed shape depending on which page
            you were on. Inside the shell it inherits whatever this environment's navigation is. */}
        <Route path="visits" element={<FieldApp />} />
        <Route path="callcentre" element={<AmsCallCentre />} />
        <Route path="inquiry" element={<AmsInquiry />} />
        <Route path="glossary" element={<AmsGlossary />} />
        <Route path="demo" element={<AmsDemo />} />
      </Route>

      {/* Tenderer */}
      <Route path="/bid/*" element={
        <RequirePortal portal="bid"><Shell portal="bid" /></RequirePortal>
      }>
        <Route index element={<Navigate to="dashboard" replace />} />
        <Route path="dashboard" element={<Placeholder screen="Dashboard" wave="W2" />} />
        <Route path="opportunities" element={<Placeholder screen="Opportunities" wave="W2" />} />
        <Route path="submissions/:id" element={<Placeholder screen="Submission builder" wave="W2" />} />
        <Route path="submissions/:id/check" element={<Placeholder screen="forgeBid report" wave="W3" />} />
        <Route path="submissions/:id/submit" element={<Placeholder screen="Sign and submit" wave="W2" />} />
        <Route path="library" element={<Placeholder screen="Document library" wave="W2" />} />
      </Route>

      {/* Managing Agent */}
      <Route path="/ma/*" element={
        <RequirePortal portal="ma"><Shell portal="ma" /></RequirePortal>
      }>
        <Route index element={<Navigate to="dashboard" replace />} />
        <Route path="dashboard" element={<Placeholder screen="Granted tenders" wave="W5" />} />
        <Route path="clients" element={<Placeholder screen="Client Town Councils" wave="W5" />} />
      </Route>

      {/* Auditor */}
      <Route path="/audit/*" element={
        <RequirePortal portal="audit"><Shell portal="audit" /></RequirePortal>
      }>
        <Route index element={<Navigate to="dashboard" replace />} />
        <Route path="dashboard" element={<Placeholder screen="Assigned reviews" wave="W9" />} />
        <Route path="tenders/:id/trail" element={<Placeholder screen="Audit trail + chain verification" wave="W9" />} />
      </Route>

      {/* Developer */}
      <Route path="/dev/*" element={
        <RequirePortal portal="dev"><Shell portal="dev" /></RequirePortal>
      }>
        <Route index element={<Navigate to="dashboard" replace />} />
        <Route path="dashboard" element={<Placeholder screen="Platform health" wave="W1" />} />
        <Route path="templates" element={<Placeholder screen="Template libraries" wave="W1" />} />
        <Route path="prompts" element={<Placeholder screen="Prompt registry" wave="W6" />} />
      </Route>

      {/* Kept as a redirect, not deleted: an APK is already installed on phones pointing here, and
          a bookmark that 404s is a field officer who cannot work. It resolves to the real page. */}
      <Route path="/field" element={<Navigate to="/ams/visits" replace />} />

      {/* A REAL ROUTE, because clearing the stored identity does not sign anyone out.
          "Switch user" used to do exactly that and then reload — but with auth bypassed the server
          falls back to TOWNFORGE_DEV_USER_ID, so the browser was immediately signed back in as the
          configured default and landed on the same screen it started from. It looked like a dead
          button. The picker had to become somewhere you can GO, not a state you fall into when
          something fails. */}
      <Route path="/signin" element={<SignIn />} />

      {/* Public — no login. Someone challenging an award is usually not a user, and a verifier
          that requires an account verifies nothing to the person who most needs it. */}
      <Route path="/verify" element={<Placeholder screen="Public signature and snapshot verifier" wave="W8" />} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}
